'use client';
import { useState, useEffect, useMemo, Fragment } from 'react';
import type { MouseEvent, KeyboardEvent } from 'react';
import { useRouter, usePathname } from 'src/navigation';
import { cleanURLParams } from 'helpers/search/cleanURLParams';
import PublicationSearchCard from 'components/publications/publication-search-card';
import PageSection from 'components/containers/page-section';
import Subsection from 'components/containers/sub-section';
import PageHeader from 'components/headers/page-header';
import SearchBar from 'components/controls/search-bar';
import PaginationControls from 'components/pagination';
import styles from './publications-search.module.scss';
import type { estypes } from '@elastic/elasticsearch';
import type {
	IPublicationSearchParams,
	IPublicationSearchAppProps,
} from '@metmuseum/types';
import { sortByFields } from './_sort-filters';
import ClearableFilters, {
	type IClearableFiltersProps,
} from 'components/controls/filters/clearable-filters';
import CollapsibleDrawer from 'components/collapsible-drawer';
import CheckboxWithLabel from 'components/controls/checkbox-with-label';
import CheckboxGroupHeading from 'components/controls/checkbox-group-heading';
import NoResults from 'components/search/no-results';
import SearchLayout from 'components/search/search-layout';
import DebuggerInfo from 'components/search/debugger-info';
import SortBy from 'components/search/sort-by';
import ResultsCount from 'components/search/results-count';
import SortAndResultsSection from 'components/search/sort-and-results-section';
import ActiveFiltersSection from 'components/search/active-filters-section';
import CheckBoxGroups from 'components/search/checkbox-groups';
import CheckboxSubGroups from 'components/search/checkbox-sup-groups';

const PER_PAGE = 10;
const ARRAY_PARAMS = [
	'departments',
	'metPubsTopics',
	'metPubsTopicGroups',
	'categories',
	'categoryGroups',
	'waysToRead',
];

const deserializeParams = (params: IPublicationSearchParams) => {
	Object.keys(params).forEach((key) => {
		if (ARRAY_PARAMS.includes(key)) {
			params[key] = `${params[key]}`.split(',');
		}
	});
	return params;
};

const sortByName = (a, b) => a.name.localeCompare(b.name);

const PublicationsSearchApp = (
	props: estypes.SearchResponse<any, any> & IPublicationSearchAppProps,
) => {
	const pathname = usePathname();
	const router = useRouter();
	const [params, setParams] = useState(deserializeParams(props.searchParams));
	const [hideControls, setHideControls] = useState(false);
	const page = props.searchParams?.page || 1;
	const findFilter = (param: string, slug: string) => {
		const bySlug = (filter) => filter.slug.current === slug;

		const filter =
			props.filters[param]?.find(bySlug) || // top level
			props.filters.metPubsTopicGroups.reduce((result, group) => {
				// TODO: refactor as like "children?"
				return result || group.topics?.find(bySlug);
			}, undefined) ||
			props.filters.categoryGroups.reduce((result, group) => {
				return result || group.categories?.find(bySlug);
			}, undefined);

		return filter;
	};

	const isChecked = (name, slug) => {
		return params?.[name]?.includes(slug);
	};

	const offset = page * PER_PAGE - PER_PAGE;

	const getCountBySlug = ({
		type,
		slug,
	}: {
		type: string;
		slug: string;
	}): number => {
		// there might be data in Sanity that is unused, and so it doesn'tt appear in the Elastic aggs, so we need ?.doc_count || 0
		return (
			props.aggregations[type].slugs.buckets.find(
				(bucket) => bucket.key === slug,
			)?.doc_count || 0
		);
	};

	// I'm not sure we want to do this all the time, everywhere, but it works sufficiently well for this case?
	const handleMultiCheckboxChange = (e) => {
		const { name, value, checked } = e.target;

		// obligatory resetting of the params
		const newParams = { ...params, page: 1 };

		// handle the checkbox change
		const currentValues = newParams?.[name] || [];
		if (checked) {
			currentValues?.push(value);
		} else {
			// remove
			const index = currentValues.indexOf(value);
			currentValues.splice(index, 1);
		}

		newParams[name] = currentValues;

		// handle xor for parent/child checkboxes
		// note: this causes an extremely dumb warning in react about these components changing from controller to uncontrolled... idk.. deal with it, React?
		// const filter = props.filters[name]?.find(filter => filter.slug.current === value)
		const filter = findFilter(name, value);

		if (checked && filter?.selects) {
			filter.selects.forEach((thingToUnselect) => {
				Object.entries(thingToUnselect).forEach(
					([paramName, slug]: [string, string]) => {
						if (newParams[paramName]) {
							newParams[paramName].push(slug);
							newParams[paramName] = [...new Set(newParams[paramName])];
						} else {
							newParams[paramName] = [slug];
						}
					},
				);
			});
		}

		if (!checked && filter?.unselects) {
			filter.unselects.forEach((thingToUnselect) => {
				Object.entries(thingToUnselect).forEach(
					([paramName, slug]: [string, string]) => {
						if (newParams[paramName]) {
							newParams[paramName] = newParams[paramName].filter(
								(val) => val !== slug,
							);
						}
					},
				);
			});
		}

		setParams(newParams);
	};

	const handlePageChange = (e) => {
		window.scrollTo(0, 0);
		const newPage = e.currentTarget.value / PER_PAGE + page;
		const newParams = { ...params, page: newPage };
		setParams(newParams);
	};

	const handleSortParamChange = (event) => {
		const newParams = { ...params, page: 1, sortBy: event.target.value };
		setParams(newParams);
	};

	const handleSearchChange = (e) => {
		const query = e.target.value;
		const newParams = { ...params, q: query, page: 1 };
		// if search is present, we always reset to relevance
		if (query && newParams.sortBy !== 'relevance') {
			delete newParams.sortBy;
		}
		// if search is empty, we default back to publicationDateDesc
		if (!query && newParams.sortBy === 'relevance') {
			// relevance is not an option without a text search, so remove it
			delete newParams.sortBy;
		}
		setParams(newParams);
	};

	// takes URL params and matches them to filters. this is needed to
	// 1) give them a label
	// 2) ignore any params that aren't available filters
	const mapParamsToClearableFilters = (): IClearableFiltersProps['facets'] => {
		const result = Object.keys(params)
			.map((facetName) => {
				if (
					facetName in props.filterNames &&
					Array.isArray(params[facetName])
				) {
					return {
						name: facetName,
						selectedValues: params[facetName].filter(Boolean).map((value) => ({
							label: props.filterNames[facetName][value],
							value,
						})),
					};
				}
			})
			.filter(Boolean);

		return result;
	};

	const handleClearOne = (e: MouseEvent | KeyboardEvent, facet: string) => {
		const target = e.target as HTMLButtonElement;
		const slug = target.value; // it does

		const filterToClear = findFilter(facet, slug);

		const newParams = { ...params };
		if (newParams[facet]) {
			newParams[facet] = newParams[facet].filter((val) => val !== slug);
		}

		if (filterToClear?.unselects) {
			filterToClear.unselects.forEach((filterToUnselect) => {
				Object.keys(filterToUnselect).forEach((param) => {
					if (newParams[param]) {
						newParams[param] = newParams[param].filter(
							(val) => !filterToUnselect[param].includes(val),
						);
					}
				});
			});
		}

		setParams(newParams);
	};

	const handleClearAll = () =>
		setParams({
			q: params.q,
			sortBy: params.sortBy,
		});

	const allDepartments = useMemo(() => {
		return props.filters.departments.reduce(
			(results, department) => {
				if (
					['curatorial', 'conservation'].includes(
						department?.type?.slug?.current,
					)
				) {
					results[department.type.slug.current].push(department);
				} else {
					results.other.push(department);
				}
				return results;
			},
			{
				curatorial: [],
				conservation: [],
				other: [],
			},
		);
	}, [props.filters.departments]);

	const allTopics = useMemo(() => {
		return [
			...props.filters.metPubsTopics,
			...props.filters.metPubsTopicGroups,
		].sort(sortByName);
	}, [props.filters.metPubsTopics, props.filters.metPubsTopicGroups]);

	const allCategories = useMemo(() => {
		return [...props.filters.categories, ...props.filters.categoryGroups].sort(
			sortByName,
		);
	}, [props.filters.categories, props.filters.categoryGroups]);

	useEffect(() => {
		cleanURLParams(params);
		router.push(`${pathname}?${new URLSearchParams(params as any)}`, {
			scroll: false,
		});
	}, [params, pathname, router]);

	return (
		<div>
			<PageHeader title="MetPublications">
				<SearchBar initialQuery={params['q']} onChange={handleSearchChange} />
				<DebuggerInfo took={props.took} show={!!params.debugMode}>
					params: {JSON.stringify(params)}
				</DebuggerInfo>

				<ActiveFiltersSection
					handleFilterButtonClick={() => setHideControls(!hideControls)}
				>
					<ClearableFilters
						groupName={'pubs-facets'}
						handleClearAll={handleClearAll}
						handleClose={handleClearOne}
						facets={mapParamsToClearableFilters()}
					/>
				</ActiveFiltersSection>
			</PageHeader>
			<SortAndResultsSection>
				<ResultsCount
					offset={offset}
					perPage={PER_PAGE}
					totalResults={props?.hits?.total}
					query={params.q}
				/>
				<SortBy
					initialValue={
						params?.sortBy
							? params.sortBy
							: params.q
								? 'relevance'
								: 'publicationDateDesc'
					}
					options={Object.entries(sortByFields).map(([value, { name }]) => {
						return {
							name,
							value,
							// relevance is not an option without a text search, so disable it
							isDisabled: !params.q && value === 'relevance',
						};
					})}
					handleChange={handleSortParamChange}
				/>
			</SortAndResultsSection>

			<SearchLayout
				handleOpenButton={() => setHideControls(false)}
				handleCloseButton={() => setHideControls(true)}
				hideControls={hideControls}
				results={
					<div>
						{props?.hits?.hits?.length > 0 ? (
							<ul className={styles.results}>
								{props?.hits?.hits?.map((publication) => {
									return (
										<PublicationSearchCard
											key={publication._id}
											publication={publication._source}
										/>
									);
								})}
							</ul>
						) : (
							<Subsection>
								<NoResults
									title={`No Results Found for ${params.q}`}
									body="We couldn't find any matches. Try checking your spelling, using different key words, or broadening your search."
								/>
							</Subsection>
						)}
					</div>
				}
				controls={
					<div>
						{/* Ways to read */}
						<CollapsibleDrawer
							open={true}
							heading={
								<CheckboxGroupHeading
									name="Ways to read"
									count={params?.waysToRead?.length}
								/>
							}
						>
							<CheckBoxGroups>
								{props.filters.waysToRead.map((way) => {
									const checked = isChecked('waysToRead', way.slug);
									return (
										<CheckboxWithLabel
											checked={checked}
											text={way.name}
											name={'waysToRead'}
											key={'waysToRead' + way.slug + checked}
											id={`ways-${way.slug}`}
											value={way.slug}
											onChange={handleMultiCheckboxChange}
											count={props?.aggregations?.[way.slug]?.doc_count}
										/>
									);
								})}
							</CheckBoxGroups>
						</CollapsibleDrawer>

						{/* Topics */}
						<CollapsibleDrawer
							open={false}
							heading={
								<CheckboxGroupHeading
									name="Topic"
									count={params?.metPubsTopics?.length}
								/>
							}
						>
							<CheckBoxGroups>
								{allTopics.map((topicOrGroup) => {
									let topic, topicGroup;

									if (topicOrGroup?.topics) {
										topicGroup = topicOrGroup;
									} else {
										topic = topicOrGroup;
									}

									if (topic) {
										const checked = isChecked(
											'metPubsTopics',
											topic.slug.current,
										);
										return (
											<CheckboxWithLabel
												checked={checked}
												text={topic.name}
												name={'metPubsTopics'}
												key={'topics' + topic.slug.current + checked}
												id={`topics-${topic.slug.current}`}
												value={topic.slug.current}
												onChange={handleMultiCheckboxChange}
												count={getCountBySlug({
													type: 'metPubsTopics',
													slug: topic.slug.current,
												})}
											/>
										);
									}

									if (topicGroup) {
										const groupIsChecked =
											isChecked(
												'metPubsTopicGroups',
												topicGroup.slug.current,
											) ||
											topicGroup.topics.every((topic) =>
												isChecked('metPubsTopics', topic.slug.current),
											);
										return (
											<Fragment
												key={`topic-group-${topicGroup.slug.current}-${groupIsChecked}`}
											>
												<fieldset>
													<CheckboxWithLabel
														checked={groupIsChecked}
														indeterminate={
															topicGroup.topics.some((topic) =>
																isChecked('metPubsTopics', topic.slug.current),
															) &&
															topicGroup.topics.some(
																(topic) =>
																	!isChecked(
																		'metPubsTopics',
																		topic.slug.current,
																	),
															)
														}
														text={topicGroup.name}
														name={'metPubsTopicGroups'}
														id={`category-${`topic-group-${topicGroup.slug.current}`}`}
														value={topicGroup.slug.current}
														onChange={handleMultiCheckboxChange}
														count={getCountBySlug({
															type: 'metPubsTopicGroups',
															slug: topicGroup.slug.current,
														})}
													/>
												</fieldset>
												<CheckboxSubGroups>
													{topicGroup.topics.map((topic) => {
														const checked = isChecked(
															'metPubsTopics',
															topic.slug.current,
														);
														return (
															<CheckboxWithLabel
																key={`topics-${topic.slug.current}-${checked}`} // don't change this key unless you know why it's already like this
																checked={checked}
																text={topic.name}
																name={'metPubsTopics'}
																value={topic.slug.current}
																id={`category-${`topics-${topic.slug.current}`}`}
																onChange={handleMultiCheckboxChange}
																count={getCountBySlug({
																	type: 'metPubsTopics',
																	slug: topic.slug.current,
																})}
															/>
														);
													})}
												</CheckboxSubGroups>
											</Fragment>
										);
									}
								})}
							</CheckBoxGroups>
						</CollapsibleDrawer>

						{/* Publication Type */}
						<CollapsibleDrawer
							open={false}
							heading={
								<CheckboxGroupHeading
									name="Publication type"
									count={params?.categories?.length}
								/>
							}
						>
							<CheckBoxGroups>
								{allCategories.map((categoryOrGroup) => {
									let category, categoryGroup;

									if (categoryOrGroup?.categories) {
										categoryGroup = categoryOrGroup;
									} else {
										category = categoryOrGroup;
									}

									if (category) {
										// top level categories are safe to loop through
										const checked = isChecked(
											'categories',
											category.slug.current,
										);
										return (
											<CheckboxWithLabel
												checked={checked}
												text={category.name}
												name={'categories'}
												value={category.slug.current}
												key={`category-${category.slug.current}-${checked}`}
												id={`category-${category.slug.current}`}
												onChange={handleMultiCheckboxChange}
												count={getCountBySlug({
													type: 'categories',
													slug: category.slug.current,
												})}
											/>
										);
									}

									if (
										categoryGroup &&
										categoryGroup.slug.current === 'periodicals'
									) {
										// we hardcode periodicals because of insane special logic 😅
										const groupIsChecked =
											isChecked('categoryGroup', categoryGroup.slug.current) ||
											categoryGroup.categories.every((category) =>
												isChecked('categories', category.slug.current),
											);
										const journalsCount =
											getCountBySlug({
												type: 'categories',
												slug: 'journals',
											}) +
											getCountBySlug({
												type: 'categories',
												slug: 'metropolitan-museum-journal-articles',
											});
										const bulletinsCount = getCountBySlug({
											type: 'categories',
											slug: 'metropolitan-museum-bulletins',
										});
										const bulletinsChecked = isChecked(
											'categories',
											'metropolitan-museum-bulletins',
										);
										const journalsChecked = isChecked('categories', 'journals');

										return (
											<Fragment
												key={`category-group-container-${categoryGroup.slug.current}-${groupIsChecked}`}
											>
												<fieldset>
													<CheckboxWithLabel
														checked={groupIsChecked}
														key={`category-group-${categoryGroup.slug.current}-${groupIsChecked}`}
														indeterminate={
															[
																...new Set(
																	categoryGroup.categories.map((category) =>
																		isChecked(
																			'categories',
																			category.slug.current,
																		),
																	),
																),
															].length == 2 // if there are both checked and unchecked, it's indeterminate
														}
														// indeterminate={categoryGroup.isIndeterminate}
														text={categoryGroup.name}
														name={'categoryGroups'}
														id={`category-group-${categoryGroup.slug.current}`}
														value={categoryGroup.slug.current}
														onChange={handleMultiCheckboxChange}
														count={journalsCount + bulletinsCount}
													/>
												</fieldset>
												<CheckboxSubGroups>
													{/* // bulletins are straightforward, bless their heart */}
													<CheckboxWithLabel
														key={`categories-metropolitan-museum-bulletins-${bulletinsChecked}`}
														checked={bulletinsChecked}
														text={'Metropolitan Museum Bulletins'}
														name={'categories'}
														value={'metropolitan-museum-bulletins'}
														id={'metropolitan-museum-bulletins'}
														onChange={handleMultiCheckboxChange}
														count={bulletinsCount}
													/>
													<CheckboxWithLabel
														key={`categories-journals-${journalsChecked}`}
														checked={journalsChecked}
														text={'Metropolitan Museum Journals'}
														name={'categories'}
														value={'journals'}
														id={'journals'}
														onChange={handleMultiCheckboxChange}
														count={journalsCount}
													/>
												</CheckboxSubGroups>
											</Fragment>
										);
									}
								})}
							</CheckBoxGroups>
						</CollapsibleDrawer>

						{/* Department */}
						<CollapsibleDrawer
							open={false}
							heading={
								<CheckboxGroupHeading
									name="Met department"
									count={params?.departments?.length}
								/>
							}
						>
							{['Curatorial', 'Conservation', 'Other'].map(
								(departmentGroup) => {
									return (
										<CheckBoxGroups key={`dept-group-${departmentGroup}`}>
											<h5>{departmentGroup}</h5>
											{allDepartments[departmentGroup.toLowerCase()].map(
												(department) => {
													const checked = isChecked(
														'departments',
														department.slug.current,
													);
													return (
														<CheckboxWithLabel
															checked={checked}
															text={department.displayName}
															name={'departments'}
															value={department.slug.current}
															key={`department-${department.slug.current}-${checked}`}
															id={`department-${department.slug.current}`}
															onChange={handleMultiCheckboxChange}
															count={getCountBySlug({
																type: 'departments',
																slug: department.slug.current,
															})}
														/>
													);
												},
											)}
										</CheckBoxGroups>
									);
								},
							)}
						</CollapsibleDrawer>
					</div>
				}
			></SearchLayout>
			<PageSection>
				<PaginationControls
					offset={offset}
					handlePaginationChange={handlePageChange}
					perPage={PER_PAGE}
					totalResults={props?.hits?.total}
				/>
			</PageSection>
		</div>
	);
};

export default PublicationsSearchApp;
